import React, { useMemo, useState } from 'react';
import { Table, Modal, List, Spin, Typography, Space, Tag, message } from 'antd';
import { CheckCircleTwoTone, ExclamationCircleTwoTone } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ShelfAllocations from './ShelfAllocations';
import { formatBoxPreview } from './boxPreview';
import { useReceiptPutaway } from '@contexts/operator/ReceiptPutawayContext';
import receiptService from '@services/operator/receiptService';
import { CustomAttributesTags } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags';

const { Text } = Typography;

interface RecommendState {
  open: boolean;
  loading: boolean;
  itemId: string | null;
  results: Recommendation[];
}

interface Recommendation {
  shelf_id: string;
  shelf_code: string;
  reasons?: string[];
  [key: string]: unknown;
}

interface PutawayItem {
  id: string;
  product_name?: string;
  product_sku?: string;
  custom_attributes?: Record<string, unknown>;
  batch?: string;
  expiration_date?: string;
  quantity?: number;
  putaway_quantity?: number;
  quantity_per_box?: number;
  [key: string]: unknown;
}

const PutawayTable: React.FC = () => {
  const { t } = useTranslation();
  // useReceiptPutaway 來自 .jsx context，TS 從 createContext(null) + 守衛
  // 把回傳 narrow 成 never；用 any cast 還原 .jsx 邊界該有的「未知形狀」行為。
  const { state, actions } = useReceiptPutaway() as any;
  const warehouseId = state.receipt?.warehouse?.id;

  const sortedItems = useMemo<PutawayItem[]>(() => {
    const pending: PutawayItem[] = [];
    const done: PutawayItem[] = [];
    state.items.forEach((it: PutawayItem) => {
      const remaining = (it.quantity || 0) - (it.putaway_quantity || 0);
      (remaining > 0 ? pending : done).push(it);
    });
    return [...pending, ...done];
  }, [state.items]);

  const [recommendState, setRecommendState] = useState<RecommendState>({
    open: false, loading: false, itemId: null, results: [],
  });

  const openRecommend = async (itemId: string) => {
    setRecommendState({ open: true, loading: true, itemId, results: [] });
    try {
      const body = await receiptService.recommend(itemId);
      setRecommendState({ open: true, loading: false, itemId, results: body?.data || [] });
    } catch (err) {
      // 網路錯誤 / 5xx / parse 失敗都要讓使用者知道，不要與「真正無推薦」混淆
      message.error(t('operator.pages.putaway.modals.recommendations_error', {
        defaultValue: '推薦儲位載入失敗，請重試',
      }));
      setRecommendState({ open: false, loading: false, itemId: null, results: [] });
    }
  };

  const applyRecommendation = (rec: Recommendation) => {
    const { itemId } = recommendState;
    const item = state.items.find((i: PutawayItem) => i.id === itemId);
    if (!item) { setRecommendState(s => ({ ...s, open: false })); return; }
    const remaining = (item.quantity || 0) - (item.putaway_quantity || 0);
    const firstRowKey = (state.rowStates[itemId as string] || [])[0]?.key;
    if (firstRowKey) {
      actions.updateShelfRow(itemId, firstRowKey, {
        shelfId: rec.shelf_id,
        location: rec.shelf_code,
        quantity: remaining,
      });
    }
    setRecommendState({ open: false, loading: false, itemId: null, results: [] });
  };

  const selectablePending = sortedItems.filter((it: PutawayItem) =>
    (it.quantity || 0) - (it.putaway_quantity || 0) > 0
  );

  const rowSelection = {
    selectedRowKeys: Array.from(state.selection) as string[],
    onSelect: (record: PutawayItem) => actions.toggleSelect(record.id),
    onSelectAll: (selected: boolean) => {
      if (selected) {
        selectablePending.forEach((it: PutawayItem) => {
          if (!state.selection.has(it.id)) actions.toggleSelect(it.id);
        });
      } else {
        actions.clearSelection();
      }
    },
    getCheckboxProps: (record: PutawayItem) => {
      const remaining = (record.quantity || 0) - (record.putaway_quantity || 0);
      return { disabled: remaining <= 0 };
    },
  };

  // ShelfAllocations 仍是 .jsx，TS 從其 destructure 推導出的 props 不完整，
  // 用 ComponentType<any> 包一層保留原本 runtime 行為。
  // 放在 component body 內以避免 Vite HMR 下的 top-level TDZ。
  const ShelfAllocationsComp = ShelfAllocations as React.ComponentType<any>;

  const columns = [
    {
      title: t('operator.pages.putaway.columns.product'),
      key: 'product',
      width: 260,
      render: (_: unknown, item: PutawayItem) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ lineHeight: '20px' }}>{item.product_name}</Text>
          <Text type="secondary" style={{ fontSize: 12, lineHeight: '18px' }}>{item.product_sku}</Text>
          <CustomAttributesTags attributes={item.custom_attributes} emptyFallback={null} />
        </Space>
      ),
    },
    {
      title: t('operator.pages.putaway.columns.batch_expiry'),
      key: 'batch_expiry',
      width: 130,
      render: (_: unknown, item: PutawayItem) => {
        if (!item.batch && !item.expiration_date) return <Text type="secondary">—</Text>;
        return (
          <Space direction="vertical" size={0}>
            {item.batch && <Text style={{ fontSize: 13 }}>{item.batch}</Text>}
            {item.expiration_date && (
              <Text type="secondary" style={{ fontSize: 12 }}>{item.expiration_date}</Text>
            )}
          </Space>
        );
      },
    },
    {
      title: t('operator.pages.putaway.columns.to_putaway', { defaultValue: '應上架' }),
      key: 'to_putaway',
      width: 96,
      align: 'right' as const,
      render: (_: unknown, item: PutawayItem) => {
        const remaining = (item.quantity || 0) - (item.putaway_quantity || 0);
        const isCompleted = remaining <= 0;
        if (isCompleted) {
          return (
            <Tag icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} color="success">
              {t('operator.pages.putaway.status.completed')}
            </Tag>
          );
        }
        const partial = (item.putaway_quantity || 0) > 0;
        const boxPreview = formatBoxPreview(remaining, item.quantity_per_box, t);
        return (
          <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
            <Text strong style={{ fontSize: 16 }}>{remaining}</Text>
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 2 }}>
              {t('operator.pages.putaway.columns.units', { defaultValue: '件' })}
            </Text>
            {boxPreview && (
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>{boxPreview}</Text>
              </div>
            )}
            {partial && (
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {t('operator.pages.putaway.columns.of_total', { total: item.quantity, defaultValue: '共 {{total}}' })}
                </Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('operator.pages.putaway.columns.shelf_allocation', { defaultValue: '上架配置' }),
      key: 'allocation',
      width: 420,
      render: (_: unknown, item: PutawayItem) => {
        const remaining = (item.quantity || 0) - (item.putaway_quantity || 0);
        if (remaining <= 0) return <Text type="secondary">—</Text>;
        return (
          <ShelfAllocationsComp
            itemId={item.id}
            remaining={remaining}
            quantityPerBox={item.quantity_per_box}
            warehouseId={warehouseId}
            onRecommend={openRecommend}
          />
        );
      },
    },
  ];

  const rowClassName = (item: PutawayItem) => {
    const remaining = (item.quantity || 0) - (item.putaway_quantity || 0);
    const result = state.results?.[item.id];
    if (remaining <= 0) return 'putaway-row-completed';
    if (result?.success === true) return 'putaway-row-result-success';
    if (result?.success === false) return 'putaway-row-result-failure';
    return '';
  };

  const expandedRowRender = (item: PutawayItem) => {
    const result = state.results?.[item.id];
    if (!result || result.success !== false) return null;
    return (
      <Space size={8}>
        <ExclamationCircleTwoTone twoToneColor="#ff4d4f" />
        <Text type="danger">
          {t([`operator.pages.putaway.errors.${result.message}`, `errors.${result.message}`], {
            defaultValue: result.message,
            ...(result.interpolations || {}),
          }) as string}
        </Text>
      </Space>
    );
  };

  const hasFailures = useMemo(
    () => state.results && Object.values(state.results).some((r: any) => r.success === false),
    [state.results]
  );

  return (
    <>
      <style>{`
        .putaway-row-completed td { background: #fafafa !important; color: #999; }
        .putaway-row-result-success td { background: #f6ffed !important; }
        .putaway-row-result-failure td { background: #fff1f0 !important; }
        .ant-table-expanded-row > td { background: #fff1f0 !important; padding: 6px 16px !important; }
      `}</style>
      <Table
        rowKey="id"
        dataSource={sortedItems}
        columns={columns}
        pagination={false}
        rowSelection={rowSelection}
        rowClassName={rowClassName}
        size="middle"
        bordered={false}
        expandable={hasFailures ? {
          expandedRowRender,
          rowExpandable: (item: PutawayItem) => {
            const result = state.results?.[item.id];
            return !!result && result.success === false;
          },
          defaultExpandAllRows: true,
          showExpandColumn: false,
        } : undefined}
      />

      <Modal
        open={recommendState.open}
        onCancel={() => setRecommendState({ open: false, loading: false, itemId: null, results: [] })}
        footer={null}
        title={t('operator.pages.putaway.modals.recommendations_title')}
        width={520}
      >
        {recommendState.loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
        ) : recommendState.results.length === 0 ? (
          <Text type="secondary">{t('operator.pages.putaway.modals.no_recommendations')}</Text>
        ) : (
          <List
            dataSource={recommendState.results}
            renderItem={(rec: Recommendation, idx: number) => {
              // Only show positive reasons. Penalty reasons (e.g.
              // penalty_save_empty_for_bulk) are internal ranking signals —
              // the score already reflects them; surfacing the label as a
              // tag just adds noise for operators.
              const positiveReasons = (rec.reasons || []).filter(r => !r.startsWith('penalty_'));
              return (
                <List.Item
                  actions={[
                    <a key="use" onClick={() => applyRecommendation(rec)}>
                      {t('operator.pages.putaway.actions.use')}
                    </a>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space size={8}>
                        <Text type="secondary" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                          #{idx + 1}
                        </Text>
                        <Text strong style={{ fontSize: 15 }}>{rec.shelf_code}</Text>
                      </Space>
                    }
                    description={
                      positiveReasons.length > 0 ? (
                        <Space size={4} wrap>
                          {positiveReasons.map(code => (
                            <Tag key={code} color="blue" style={{ margin: 0 }}>
                              {t(`operator.pages.putaway.recommendation_reasons.${code}`, { defaultValue: code })}
                            </Tag>
                          ))}
                        </Space>
                      ) : null
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Modal>
    </>
  );
};

export default PutawayTable;
